import type { ButtonProps } from '@sravni/react-design-system/lib/Button';
import { useCallback, useState } from 'react';
import { v7 as uuidv7 } from 'uuid';

import type { MPLPreApprovalOffer } from 'commonTypes/api/scenarios/CreditSelectionScreenContent';

import { isMplPreApprovalOffer, isPreApprovalOffer } from 'shared/lib';
import type { TOffer } from 'shared/types/TRankingService';

// eslint-disable-next-line boundaries/element-types
import { IncomeConfirmationModal } from 'features/IncomeConfirmationModal';

import { useSendOffer } from '../../lib';
import { getOfferButtonText } from '../../lib/getOfferButtonText';
import { OfferButton } from '../OfferButton/OfferButton';

interface ISendOfferProps {
  offer: TOffer | MPLPreApprovalOffer;
  buttonProps?: ButtonProps;
}

export const SendOffer: FC<ISendOfferProps> = (props) => {
  const { offer, buttonProps } = props;
  const { onClick: onClickProp, loading: isLoadingProp, ...otherButtonProps } = buttonProps || {};
  const { sendOffer, sendPreApprovalOffer, sendMplPreApprovalOffer, isLoading } = useSendOffer();
  const [isIncomeConfirmationVisible, setIsIncomeConfirmationVisible] = useState(false);
  const shouldShowIncomeConfirmation = !isMplPreApprovalOffer(offer) && offer.hasMplApplicationAlternative;

  const onClick = async () => {
    if (isMplPreApprovalOffer(offer)) {
      if (!offer.apiStatus) {
        const uuid = uuidv7();
        await sendMplPreApprovalOffer(offer, { uuid });
      }
      return;
    }

    if (isPreApprovalOffer(offer)) {
      if (!offer.loansStatus) {
        const uuid = uuidv7();
        await sendPreApprovalOffer(offer, { uuid });
      }
      return;
    }

    if (offer.hasMplApplicationAlternative) {
      setIsIncomeConfirmationVisible(true);
      return;
    }

    await sendOffer(offer);
  };

  const onCloseIncomeConfirmation = useCallback(() => {
    setIsIncomeConfirmationVisible(false);
  }, []);

  const onSendRegularApplication = useCallback(async () => {
    if (isMplPreApprovalOffer(offer) || isPreApprovalOffer(offer)) {
      return;
    }

    await sendOffer(offer);

    setIsIncomeConfirmationVisible(false);
  }, [offer, sendOffer]);

  return (
    <>
      <OfferButton
        {...otherButtonProps}
        variant="primary"
        color="dark"
        onClick={async (event) => {
          event.stopPropagation();
          await onClick();
          onClickProp?.(event);
        }}
        loading={isLoading || isLoadingProp || isIncomeConfirmationVisible}
      >
        {getOfferButtonText(offer)}
      </OfferButton>
      {shouldShowIncomeConfirmation && !isMplPreApprovalOffer(offer) ? (
        <IncomeConfirmationModal
          visible={isIncomeConfirmationVisible}
          offer={offer}
          isSubmitting={isLoading}
          onClose={onCloseIncomeConfirmation}
          onSendRegularApplication={onSendRegularApplication}
        />
      ) : null}
    </>
  );
};
